import { c as _c } from "react/compiler-runtime";
import { Button } from 'design-system/client';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useEffectEvent, useRef, useState } from 'react';
import { AudioState, useAudio } from '@/helpers/hooks';
import type { Maybe } from '@/types';
import { isNotNullOrUndefined } from 'betterplace-utils';
export type GifAndSoundProps = {
  id?: string | number | null;
  children?: ReactNode;
  duration: number;
  soundSrc?: Maybe<string>;
  volume: number;
  playWithSound: boolean;
  onPlaybackStart: ((id: string | number | null) => void) | undefined;
  onPlaybackComplete: ((id: string | number | null) => void) | undefined;
  gifSrc?: Maybe<string>;
  gifHeight: number;
  skipPlayback?: boolean;
};
function Gif(t0) {
  const $ = _c(5);
  const {
    src,
    gifHeight
  } = t0;
  if (!src) {
    let t1;
    if ($[0] !== gifHeight) {
      t1 = <div style={{
        height: gifHeight
      }} />;
      $[0] = gifHeight;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== gifHeight || $[3] !== src) {
    t1 = <img src={src} alt="celebration" className="streambot-image" height={gifHeight} />;
    $[2] = gifHeight;
    $[3] = src;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  return t1;
}
function GifAndSound(t0) {
  const $ = _c(41);
  const {
    children,
    duration,
    soundSrc,
    volume,
    playWithSound,
    onPlaybackComplete,
    onPlaybackStart,
    id,
    gifSrc,
    gifHeight,
    skipPlayback
  } = t0;
  const t = useTranslations("nextjs");
  const timeoutRef = useRef(null);
  const [showAlert, setShowAlert] = useState(false);
  const durationMs = (isNaN(duration) ? 5 : duration) * 1000;
  let t1;
  if ($[0] !== playWithSound || $[1] !== soundSrc || $[2] !== volume) {
    t1 = {
      source: soundSrc,
      volume,
      playWithSound
    };
    $[0] = playWithSound;
    $[1] = soundSrc;
    $[2] = volume;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const {
    play,
    stop,
    state
  } = useAudio(t1);
  let t2;
  if ($[4] !== onPlaybackComplete || $[5] !== stop) {
    t2 = id_0 => {
      setShowAlert(false);
      onPlaybackComplete?.(id_0);
      stop();
      if (timeoutRef.current === null) {
        return;
      }
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    };
    $[4] = onPlaybackComplete;
    $[5] = stop;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const completeAlert = t2;
  let t3;
  if ($[7] !== completeAlert || $[8] !== durationMs) {
    t3 = function handlePlayback(id_1) {
      timeoutRef.current = setTimeout(() => {
        completeAlert(id_1);
      }, durationMs);
    };
    $[7] = completeAlert;
    $[8] = durationMs;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  const handlePlayback = t3;
  let t4;
  if ($[10] !== handlePlayback || $[11] !== onPlaybackStart || $[12] !== play || $[13] !== playWithSound) {
    t4 = id_2 => {
      setShowAlert(true);
      onPlaybackStart?.(id_2);
      if (playWithSound) {
        play(undefined, () => handlePlayback(id_2));
        return;
      }
      handlePlayback(id_2);
    };
    $[10] = handlePlayback;
    $[11] = onPlaybackStart;
    $[12] = play;
    $[13] = playWithSound;
    $[14] = t4;
  } else {
    t4 = $[14];
  }
  const startPlayback = t4;
  const startEffect = useEffectEvent(startPlayback);
  const completeEffect = useEffectEvent(completeAlert);
  let t5;
  if ($[15] !== completeEffect || $[16] !== id || $[17] !== skipPlayback || $[18] !== startEffect) {
    t5 = () => {
      if (!isNotNullOrUndefined(id)) {
        return;
      }
      if (skipPlayback) {
        return;
      }
      startEffect(id);
      const id_ = id;
      return () => completeEffect(id_);
    };
    $[15] = completeEffect;
    $[16] = id;
    $[17] = skipPlayback;
    $[18] = startEffect;
    $[19] = t5;
  } else {
    t5 = $[19];
  }
  let t6;
  if ($[20] !== id || $[21] !== skipPlayback) {
    t6 = [id, skipPlayback];
    $[20] = id;
    $[21] = skipPlayback;
    $[22] = t6;
  } else {
    t6 = $[22];
  }
  useEffect(t5, t6);
  const showPermissionButton = (playWithSound || volume !== 0) && state === AudioState.PermissionRequired;
  if (showPermissionButton) {
    let t7;
    if ($[23] !== id || $[24] !== startPlayback) {
      t7 = () => {
        if (!isNotNullOrUndefined(id)) {
          return;
        }
        startPlayback(id);
      };
      $[23] = id;
      $[24] = startPlayback;
      $[25] = t7;
    } else {
      t7 = $[25];
    }
    let t8;
    if ($[26] !== t) {
      t8 = t("streambot.live_widgets.give_playback_permission");
      $[26] = t;
      $[27] = t8;
    } else {
      t8 = $[27];
    }
    let t9;
    if ($[28] !== t7 || $[29] !== t8) {
      t9 = <Button kind="secondary" type="button" className="permission-button" onClick={t7}>{t8}</Button>;
      $[28] = t7;
      $[29] = t8;
      $[30] = t9;
    } else {
      t9 = $[30];
    }
    return t9;
  }
  const t7 = showAlert ? "visible" : "hidden";
  let t8;
  if ($[31] !== t7) {
    t8 = {
      visibility: t7
    };
    $[31] = t7;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== gifHeight || $[34] !== gifSrc || $[35] !== showAlert) {
    t9 = gifSrc && <Gif gifHeight={gifHeight} src={showAlert ? gifSrc : null} />;
    $[33] = gifHeight;
    $[34] = gifSrc;
    $[35] = showAlert;
    $[36] = t9;
  } else {
    t9 = $[36];
  }
  let t10;
  if ($[37] !== children || $[38] !== t8 || $[39] !== t9) {
    t10 = <div style={t8}>{t9}{children}</div>;
    $[37] = children;
    $[38] = t8;
    $[39] = t9;
    $[40] = t10;
  } else {
    t10 = $[40];
  }
  return t10;
}
export default GifAndSound;